import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #addonVariantCache = new Map();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // Buscar si esta línea pertenece a un bundle (garantía extendida)
    const productRow = this.refs.cartItemRows[line - 1];
    const bundleId = productRow?.dataset.bundleId;
    if (bundleId) {
      return this.#updateBundleQuantities(line, quantity, bundleId);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Actualiza las cantidades de todas las líneas de un bundle (producto + garantía).
   * Usa /cart/change.js secuencialmente para cada línea (mismo endpoint que updateQuantity).
   * @param {number} productLine - Línea del producto principal (1-based)
   * @param {number} quantity - Nueva cantidad
   * @param {string} bundleId - ID del bundle
   */
  #updateBundleQuantities(productLine, quantity, bundleId) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('change:user-action');

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    const sectionsStr = Array.from(sectionsToUpdate).join(',');

    // Encontrar todas las líneas del bundle en orden
    const bundleLines = [productLine];
    const rows = this.refs.cartItemRows;
    for (let i = 0; i < rows.length; i++) {
      if (i !== productLine - 1 && rows[i].dataset.bundleId === bundleId) {
        bundleLines.push(i + 1);
      }
    }

    cartTotal?.shimmer();

    // Marcar shimmer en las filas del bundle
    for (const line of bundleLines) {
      const row = rows[line - 1];
      if (row) {
        const textComponent = /** @type {TextComponent | undefined} */ (row.querySelector('text-component'));
        textComponent?.shimmer();
      }
    }

    // Llamadas secuenciales a /cart/change.js para cada línea del bundle
    this.#changeBundleLines(bundleLines, quantity, sectionsStr, 0)
      .then((lastResponse) => {
        if (!lastResponse) return;

        resetShimmer(this);

        if (lastResponse.errors) {
          this.#handleCartError(productLine, lastResponse);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          lastResponse.sections?.[this.sectionId] || '',
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(lastResponse);

        this.dispatchEvent(
          new CartUpdateEvent(lastResponse, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: lastResponse.sections,
          })
        );

        if (lastResponse.sections?.[this.sectionId]) {
          morphSection(this.sectionId, lastResponse.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
        }

        void this.#syncProtectedPurchaseFromCart(lastResponse);

        this.#updateCartQuantitySelectorButtonStates();
        this.#notifyDrawerAfterMorph();
      })
      .catch((error) => {
        console.error('[bundle] Error updating bundle quantities:', error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Cambia secuencialmente las líneas de un bundle usando /cart/change.js.
   * @param {number[]} lines - Array de números de línea (1-based)
   * @param {number} quantity - Nueva cantidad
   * @param {string} sectionsStr - Secciones a actualizar
   * @param {number} index - Índice actual en el array
   * @returns {Promise<Object|null>} La respuesta del último cambio
   */
  #changeBundleLines(lines, quantity, sectionsStr, index) {
    if (index >= lines.length) return Promise.resolve(null);

    const body = JSON.stringify({
      line: lines[index],
      quantity,
      sections: sectionsStr,
      sections_url: window.location.pathname,
    });

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsed = JSON.parse(responseText);

        if (parsed.errors || index === lines.length - 1) {
          // Error o última línea: devolver esta respuesta
          return parsed;
        }

        // Continuar con la siguiente línea
        return this.#changeBundleLines(lines, quantity, sectionsStr, index + 1);
      });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    // 1. OBTENER INFORMACIÓN PARA UI Y BUNDLES ANTES DE MUTAR EL DOM O ESPERAR RED
    const bundleId = cartItemRowToRemove.dataset.bundleId;
    /** @type {string[]} */
    let bundleKeysToRemove = [cartItemRowToRemove.dataset.key || ''];

    if (bundleId) {
      const rows = this.refs.cartItemRows;
      for (let i = 0; i < rows.length; i++) {
        if (i !== line - 1 && rows[i].dataset.bundleId === bundleId) {
          bundleKeysToRemove.push(rows[i].dataset.key || '');
        }
      }
    }

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      ...this.refs.cartItemRows.filter(
        (row) => bundleId && row !== cartItemRowToRemove && row.dataset.bundleId === bundleId
      ),
    ];

    // 2. ACTUALIZACIÓN OPTIMISTA DE LA INTERFAZ
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;
    const template = document.getElementById('empty-cart-template');
    
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);
    } else {
      rowsToRemove.forEach((row) => {
        const remove = () => row.remove();
        if (prefersReducedMotion()) return remove();
        row.style.setProperty('--row-height', `${row.clientHeight}px`);
        row.classList.add('removing');
        onAnimationEnd(row, remove);
      });
    }

    // Si todo el carrito se vació, no es necesario actualizar garantías
    if (isEmptyCart) {
      for (const bundleKey of bundleKeysToRemove) {
        if (bundleKey) this.updateQuantity({ id: bundleKey, quantity: 0, action: 'clear' });
      }
      return;
    }

    // 3. SINCRONIZACIÓN CON EL SERVIDOR
    const cartResponse = await fetch('/cart.js');
    if (cartResponse.ok) {
      const currentCart = await cartResponse.json();
      const itemToRemove = currentCart.items[line - 1];
      
      if (itemToRemove?.properties?._Product_type === 'Protected Purchase') {
        const parentKey = itemToRemove.properties._Parent_Key;
        if (parentKey) {
          const parentIndex = currentCart.items.findIndex((/** @type {any} */ i) => i.key === parentKey || String(i.variant_id) === parentKey || String(i.id) === parentKey);
          if (parentIndex !== -1) {
            const parentProps = currentCart.items[parentIndex].properties || {};
            const newProps = { ...parentProps };
            newProps._Protected_Purchase_Eligible = '';
            newProps['_Protected_Purchase_Title'] = '';
            newProps['Compra Protegida'] = '';

            await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
              body: JSON.stringify({
                id: currentCart.items[parentIndex].key,
                quantity: currentCart.items[parentIndex].quantity,
                properties: newProps
              })
            }));
          }
        }
      }
    }

    // 4. ELIMINAR LOS ITEMS EN SHOPIFY
    for (const bundleKey of bundleKeysToRemove) {
      if (!bundleKey) continue;
      await this.updateQuantity({
        id: bundleKey,
        quantity: 0,
        action: 'clear',
      });
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(config.id ? { id: config.id } : { line: line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        void this.#syncProtectedPurchaseFromCart(parsedResponseText);

        this.#updateCartQuantitySelectorButtonStates();
        this.#notifyDrawerAfterMorph();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Notifica al cart-drawer que el section fue reemplazado (morph),
   * para que pueda restaurar el estado abierto del diálogo.
   */
  #notifyDrawerAfterMorph() {
    if (!this.isDrawer) return;
    const cartDrawer = this.closest('cart-drawer-component');
    if (cartDrawer) {
      cartDrawer.dispatchEvent(new CustomEvent('cart:section-morphed', { bubbles: false }));
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      void this.#syncProtectedPurchaseFromCart(event.detail?.resource);
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      void this.#syncProtectedPurchaseFromCart(event.detail?.resource);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#notifyDrawerAfterMorph();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });

      void this.#syncProtectedPurchaseFromCart(event.detail?.resource);
    }
  };

  /**
   * @param {string} rawConditions
   * @returns {Array<{min_price: number, max_price: number, value: number}>}
   */
  #parseProtectedPurchaseConditions(rawConditions) {
    if (!rawConditions) return [];
    try {
      const parsed = JSON.parse(rawConditions);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((item) => ({
          min_price: Number(item?.min_price || 0),
          max_price: Number(item?.max_price || 0),
          value: Number(item?.value || 0),
        }))
        .filter((item) => !Number.isNaN(item.min_price) && !Number.isNaN(item.max_price) && !Number.isNaN(item.value))
        .sort((a, b) => a.min_price - b.min_price);
    } catch (_error) {
      return [];
    }
  }

  /** @returns {string[]} */
  #getCartSectionIds() {
    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return Array.from(sectionsToUpdate);
  }

  /**
   * Obtiene o genera un customerId persistente.
   * @returns {string}
   */
  #getCustomerId() {
    const STORAGE_KEY = 'kliquea_customer_id';
    const shopify = /** @type {any} */ (window.Shopify);
    const loggedInId = shopify?.customer?.id;

    // Cliente logueado: guardar y retornar su ID real
    if (loggedInId) {
      localStorage.setItem(STORAGE_KEY, String(loggedInId));
      return String(loggedInId);
    }

    // Anónimo: recuperar ID previo o generar uno nuevo
    let anonId = localStorage.getItem(STORAGE_KEY);
    if (!anonId) {
      anonId = 'a_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
      localStorage.setItem(STORAGE_KEY, anonId);
    }
    return anonId;
  }

  /**
   * Llama al endpoint para crear/obtener dinámicamente una variante de garantía
   * y devuelve el addonVariantId.
   * @param {{productId: string, warrantyName: string, warrantyCost: string, warrantyType: string, warrantyImageUrl: string, customerId: string, shopId: string, warrantyDescription?: string}} params
   * @returns {Promise<string | null>}
   */
  async #ensureAddonVariantId({ productId, warrantyName, warrantyCost, warrantyType, warrantyImageUrl, customerId, shopId, warrantyDescription }) {
    const cacheKey = `${productId}|${warrantyName}|${warrantyCost}|${warrantyType}|${warrantyImageUrl}`;
    if (this.#addonVariantCache.has(cacheKey)) {
      return this.#addonVariantCache.get(cacheKey);
    }
    try {
      const response = await fetch('https://ldcok5ribe.execute-api.us-east-1.amazonaws.com/apps/warranty/create', {
        method: 'POST',
        signal: AbortSignal.timeout(25000),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          productId,
          warrantyName,
          warrantyCost: parseFloat(warrantyCost),
          warrantyType: warrantyType || 'fixed',
          warrantyImageUrl: warrantyImageUrl || '',
          customerId,
          shopId,
          warrantyDescription: warrantyDescription || `<p>Este artículo representa la cobertura: <strong>${warrantyName}</strong></p>`
        }),
      });
      console.log('[warranty] response:', response);
      if (!response.ok) {
        console.error('[warranty] Failed to create warranty variant:', response.status);
        return null;
      }

      const data = await response.json();
      const variantId = data?.data?.variantId || data?.variantId || data?.variant_id || data?.id;
      const variantPrice = data?.data?.price || data?.price || data?.variantPrice;
      const expectedCost = parseFloat(warrantyCost);

      if (variantId) {
        console.log('[warranty] Variant created/found:', {
          variantId,
          warrantyName,
          expectedCost,
          variantPriceFromLambda: variantPrice,
          priceMatch: variantPrice != null ? Math.abs(variantPrice - expectedCost) < 0.01 : 'unknown (no price in response)',
        });
        const id = String(variantId);
        this.#addonVariantCache.set(cacheKey, id);
        return id;
      }

      console.error('[warranty] No variant ID in response:', data);
      return null;
    } catch (error) {
      console.error('[warranty] Error creating warranty variant:', error);
      return null;
    }
  }

  /**
   * Recalcula la Compra Protegida usando subtotal de ítems elegibles
   * y condiciones por rango enviadas en `_Protected_Purchase_Conditions`.
   * @param {any} updatedCart
   */
  async #syncProtectedPurchaseFromCart(updatedCart) {
    const runtime = /** @type {any} */ (window);
    if (runtime.__protectedPurchaseSyncLock) {
      runtime.__protectedPurchaseSyncPending = true;
      return;
    }
    runtime.__protectedPurchaseSyncLock = true;

    try {
      const { cartTotal } = this.refs;
      cartTotal?.shimmer();
      this.refs.cartItemRows?.forEach((row) => {
        const text = row.querySelector('text-component');
        text?.shimmer();
      });
      let currentCart = updatedCart;
      if (!Array.isArray(currentCart?.items)) {
        const cartResponse = await fetch('/cart.js');
        if (!cartResponse.ok) return;
        currentCart = await cartResponse.json();
      }

      const items = /** @type {any[]} */ (Array.isArray(currentCart?.items) ? currentCart.items : []);
      if (!items.length) return;

      const protectedIndexes = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i]?.properties?._Product_type === 'Protected Purchase') {
          protectedIndexes.push(i);
        }
      }
      const protectedItem = protectedIndexes.length ? items[protectedIndexes[0]] : null;

      const eligibleItems = items.filter((/** @type {any} */ item) => {
        const props = item?.properties || {};
        return props._Protected_Purchase_Eligible === 'true' && props._Product_type !== 'Protected Purchase';
      });

      const sections = this.#getCartSectionIds().join(',');

      // Si no hay elegibles, eliminar cualquier línea existente de compra protegida.
      if (!eligibleItems.length) {
        let lastSections = null;
        for (let i = protectedIndexes.length - 1; i >= 0; i--) {
          const line = protectedIndexes[i] + 1;
          const removeResponse = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
            body: JSON.stringify({
              line,
              quantity: 0,
              sections,
              sections_url: window.location.pathname,
            }),
          }));
          const removeData = JSON.parse(await removeResponse.text());
          lastSections = removeData.sections;
          this.dispatchEvent(new CartUpdateEvent(removeData, this.sectionId, {
            source: 'cart-items-component',
            sections: removeData.sections,
          }));
        }
        if (lastSections?.[this.sectionId]) {
          morphSection(this.sectionId, lastSections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
        }
        return;
      }

      // Agrupar items elegibles por artículo individual (usando el key del carrito)
      const groups = {};
      eligibleItems.forEach((/** @type {any} */ item) => {
        const groupKey = item.key || item.variant_id || item.id;
        if (!groups[groupKey]) groups[groupKey] = [];
        groups[groupKey].push(item);
      });

      const itemsToAdd = [];

      for (const [groupKey, groupItems] of Object.entries(groups)) {
        const title = groupItems[0]?.properties?._Protected_Purchase_Title || groupItems[0]?.properties?.['Compra Protegida'] || 'Compra Protegida';
        const conditionsRaw = groupItems.find((/** @type {any} */ item) => item?.properties?._Protected_Purchase_Conditions)
          ?.properties?._Protected_Purchase_Conditions;
        const conditions = this.#parseProtectedPurchaseConditions(conditionsRaw || '[]');

        let selectedConditionValue = 0;
        let subtotal = 0;

        if (conditions.length > 0) {
          subtotal = groupItems.reduce((/** @type {number} */ sum, /** @type {any} */ item) => {
            const linePriceCents = Number(item?.final_line_price || item?.line_price || 0);
            return sum + (Number.isNaN(linePriceCents) ? 0 : linePriceCents / 100);
          }, 0);

          for (const condition of conditions) {
            if (subtotal >= condition.min_price && subtotal < condition.max_price) {
              selectedConditionValue = condition.value;
              break;
            }
          }
        }

        if (selectedConditionValue <= 0) {
          const costStr = groupItems.find((/** @type {any} */ item) => item?.properties?._Protected_Purchase_Cost)?.properties?._Protected_Purchase_Cost;
          selectedConditionValue = Number(costStr) || 0;
        }

        if (selectedConditionValue <= 0) continue;

        const protectedPurchaseImageUrl =
          groupItems.find((/** @type {any} */ item) => item?.properties?._Protected_Purchase_Image_URL)?.properties?._Protected_Purchase_Image_URL
          || '';

        const addonType = groupItems.find((/** @type {any} */ item) => item?.properties?._Addon_Type)?.properties?._Addon_Type || 'fixed';

        const addonVariantId = await this.#ensureAddonVariantId({
          productId: String(groupItems[0]?.variant_id || ''),
          warrantyName: title,
          warrantyCost: String(selectedConditionValue),
          warrantyType: addonType,
          warrantyImageUrl: protectedPurchaseImageUrl,
          customerId: this.#getCustomerId(),
          shopId: window.Shopify?.shop || '',
        });

        if (!addonVariantId) continue;

        const articleProperties = /** @type {Record<string, string>} */ ({});
        groupItems.forEach((/** @type {any} */ item, /** @type {number} */ index) => {
          const productTitle = item?.product_title || item?.title || `Producto ${item?.id || ''}`;
          const variantTitle = item?.variant_title || '';
          const qty = Number(item?.quantity || 1);
          const label = variantTitle ? `${productTitle} - ${variantTitle} x${qty}` : `${productTitle} x${qty}`;
          articleProperties[`Articulo ${index + 1}`] = label;
        });

        const protectedProperties = {
          _Product_type: 'Protected Purchase',
          _Addon_Name: title,
          _Addon_Type: addonType,
          _Addon_Cost_Total: Number(selectedConditionValue).toFixed(2),
          _Protected_Purchase_Subtotal: subtotal.toFixed(2),
          _Parent_Key: String(groupKey),
          ...articleProperties,
        };

        itemsToAdd.push({
          id: Number(addonVariantId),
          quantity: 1,
          properties: protectedProperties,
        });
      }

      // Eliminar TODAS las líneas actuales de compra protegida en el carrito.
      let lastSections = null;
      for (let i = protectedIndexes.length - 1; i >= 0; i--) {
        const itemToRemove = items[protectedIndexes[i]];
        const line = protectedIndexes[i] + 1;
        const removeResponse = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
          body: JSON.stringify({
            line,
            quantity: 0,
            sections,
            sections_url: window.location.pathname,
          }),
        }));
        const removeData = JSON.parse(await removeResponse.text());
        lastSections = removeData.sections;

        const isKept = itemsToAdd.some((/** @type {any} */ add) => Number(add.id) === Number(itemToRemove?.variant_id));
        if (itemToRemove?.product_id && !isKept) {
          fetch('https://ldcok5ribe.execute-api.us-east-1.amazonaws.com/apps/warranty/create', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              orderId: 'cart_removal',
              productId: String(itemToRemove.product_id),
              shopId: window.Shopify?.shop || '',
            }),
          }).catch(console.error);
        }

        if (itemsToAdd.length === 0 && i === 0) {
          this.dispatchEvent(new CartUpdateEvent(removeData, this.sectionId, {
            source: 'cart-items-component',
            sections: removeData.sections,
          }));
        }
      }

      if (itemsToAdd.length === 0) {
        if (lastSections?.[this.sectionId]) {
          morphSection(this.sectionId, lastSections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
        }
        return;
      }

      const addResponse = await this.#fetchWithRetry(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: itemsToAdd,
          sections,
        }),
      });

      const addData = JSON.parse(await addResponse.text());
      if (addData?.status) {
        console.error('[warranty] Final add to cart failed after retries:', addData);
        return;
      }

      this.dispatchEvent(new CartUpdateEvent(addData, this.sectionId, {
        source: 'cart-items-component',
        sections: addData.sections,
      }));

      if (addData.sections?.[this.sectionId]) {
        morphSection(this.sectionId, addData.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
      }
    } finally {
      resetShimmer(this);
      runtime.__protectedPurchaseSyncLock = false;
      document.dispatchEvent(new CustomEvent('protected-purchase:sync-complete'));
      if (runtime.__protectedPurchaseSyncPending) {
        runtime.__protectedPurchaseSyncPending = false;
        setTimeout(() => {
          void this.#syncProtectedPurchaseFromCart(undefined);
        }, 0);
      }
    }
  }

  /**
   * Envuelve fetch con reintentos para evitar errores 422 "No se encontró la variante"
   * @param {string} url
   * @param {RequestInit} options
   * @param {number} retries
   * @returns {Promise<Response>}
   */
  async #fetchWithRetry(url, options, retries = 6) {
    for (let i = 0; i < retries; i++) {
      const response = await fetch(url, options);
      
      if (!response.ok && response.status === 429) {
        console.warn(`[API] Retry ${i + 1}/${retries} after 429 error: rate limit`);
        if (i < retries - 1) {
          const delay = Math.pow(2, i) * 1500;
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
      }

      const clone = response.clone();
      try {
        const data = await clone.json();
        // Si hay error y es sobre variante no encontrada
        const errorText = ((data.message || '') + ' ' + (data.description || '')).toLowerCase();
        if (data.status && data.status === 422 && errorText.includes('variant')) {
          console.warn(`[API] Retry ${i + 1}/${retries} after 422 error:`, data.description || data.message);
          if (i < retries - 1) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            continue;
          }
        }
      } catch (e) {
        // Ignorar error de parseo JSON si no es JSON válido
      }
      return response;
    }
    return fetch(url, options);
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
